'use client';

import { type ButtonHTMLAttributes, type ReactNode, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/cn';

const ANIMATION_DEFAULTS = {
    enterDelay: 0.15,
    exitDelay: 0.05,
    animationDuration: 0.5,
};
export interface ShowHideItemsProps {
    items: { item: ReactNode; key: string | number }[];
    initialCount?: number;
    expandBy?: number;
    animation?: {
        enterDelay?: number;
        exitDelay?: number;
        duration?: number;
    };
    className?: string;
    itemClassName?: string;
    onAfterShowMore?: () => void;
    onAfterShowLess?: () => void;
    showMoreLabel?: string;
    showLessLabel?: string;
}
export default function ShowHideItems({
    items,
    initialCount = 3,
    expandBy = 3,
    showMoreLabel = 'Show more',
    showLessLabel = 'Show Less',
    animation,
    className,
    itemClassName,
}: ShowHideItemsProps) {
    const [visibleCount, setVisibleCount] = useState(initialCount);
    const [previousVisibleCount, setPreviousVisibleCount] = useState(0);
    const visibleItems = items.slice(0, visibleCount);
    const hasMoreItems = visibleCount < items.length;

    const enterDelay = animation?.enterDelay ?? ANIMATION_DEFAULTS.enterDelay;
    const exitDelay = animation?.exitDelay ?? ANIMATION_DEFAULTS.exitDelay;
    const animationDuration = animation?.duration ?? ANIMATION_DEFAULTS.animationDuration;

    return (
        <>
            <div className={className}>
                <AnimatePresence initial={false}>
                    {visibleItems.map(({ item, key }, i) => (
                        <ItemWrapper
                            key={key}
                            className={itemClassName}
                            enterDelay={(i - previousVisibleCount) * enterDelay}
                            exitDelay={(visibleItems.length - 1 - i) * exitDelay}
                            animationDuration={animationDuration}
                        >
                            {item}
                        </ItemWrapper>
                    ))}
                </AnimatePresence>
            </div>
            {items.length > initialCount ? (
                <AnimatePresence initial={false} mode="popLayout">
                    {hasMoreItems ? (
                        <ShowHideButton
                            data-testid="show-more-btn"
                            aria-label={`Show ${expandBy} more items`}
                            onClick={() => {
                                setPreviousVisibleCount(visibleCount);
                                setVisibleCount((prev) => Math.min(prev + expandBy, items.length));
                            }}
                        >
                            {showMoreLabel}
                        </ShowHideButton>
                    ) : (
                        <ShowHideButton
                            data-testid="show-less-btn"
                            aria-label="Show fewer items"
                            onClick={() => {
                                setPreviousVisibleCount(initialCount);
                                setVisibleCount(initialCount);
                            }}
                        >
                            {showLessLabel}
                        </ShowHideButton>
                    )}
                </AnimatePresence>
            ) : null}
        </>
    );
}

interface ItemWrapperProps {
    enterDelay?: number;
    exitDelay?: number;
    children: ReactNode;
    animationDuration?: number;
    className?: string;
}

function ItemWrapper({ children, enterDelay, exitDelay, animationDuration, className }: ItemWrapperProps) {
    return (
        <motion.div
            className={cn('overflow-hidden', className)}
            initial={{
                height: 0,
            }}
            animate={{
                height: 'auto',
                transition: {
                    height: {
                        duration: animationDuration,
                        type: 'spring',
                        delay: enterDelay,
                    },
                },
            }}
            exit={{
                height: 0,
                transition: {
                    height: {
                        duration: animationDuration,
                        type: 'spring',
                        delay: exitDelay,
                    },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

type ShowMoreButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

function ShowHideButton({ children, ...props }: ShowMoreButtonProps) {
    return (
        <motion.div
            className="flex justify-center mt-6"
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{
                transition: {
                    height: {
                        duration: 0.5,
                        type: 'spring',
                    },
                },
            }}
        >
            <button className="btn btn-primary px-7.5 py-6" {...props}>
                {children}
            </button>
        </motion.div>
    );
}
